import { apiFetch, apiFetchBlob, apiUploadFormData } from "@/lib/api";

const APPROVALS_BASE = "/api/inside-sales/approvals";

export type ApprovalKind = "order" | "quote" | "rma";
export type ApprovalColumn =
  | "processing"
  | "make_changes"
  | "preapproval"
  | "waiting_for_approval"
  | "approved"
  | "done";
export type ApprovalAiStatus = "not_started" | "processing" | "ready" | "failed";
export type ApprovalTransitionAction =
  | "submit_preapproval"
  | "preapprove"
  | "approve"
  | "request_changes"
  | "mark_sent";

export interface ApprovalQueueItem {
  id: string;
  name: string;
  description: string | null;
  kind: ApprovalKind;
  column: ApprovalColumn;
  sourceTicketNumber: string | null;
  sourceTicketUrl: string | null;
  sourceText: string | null;
  linkedAttachments: Array<{ id: string; label: string; url: string }>;
  linkedEntityId: string | null;
  linkedEntityNumber: string | null;
  assigneeUserId: string | null;
  assigneeDisplayName: string | null;
  originalDoerUserId: string | null;
  originalDoerDisplayName: string | null;
  aiStatus: ApprovalAiStatus;
  createdAt: string;
  updatedAt: string;
  clientName: string | null;
  endUserName: string | null;
}

export interface ApprovalDocument {
  id: string;
  fileName: string;
  contentType: string | null;
  sourceUrl?: string | null;
}

export interface ApprovalActivity {
  id: string;
  kind: "comment" | "history";
  action?: string;
  body: string;
  actorDisplayName: string | null;
  createdAt: string;
}

export interface ApprovalAiPayload {
  warnings?: string[];
  clientId?: string | null;
  clientName?: string | null;
  endUserId?: string | null;
  endUserName?: string | null;
  paymentTerm?: string | null;
  comments?: string | null;
  lines?: Array<{
    sku?: string | null;
    description?: string | null;
    quantity?: string | number | null;
    unitPrice?: string | number | null;
  }>;
  [key: string]: unknown;
}

export interface ApprovalDetailResponse {
  request: ApprovalQueueItem;
  documents: ApprovalDocument[];
  activity: ApprovalActivity[];
  aiStatus: ApprovalAiStatus;
  aiPayload: ApprovalAiPayload | null;
  linkedEntity: {
    id: string;
    number: string | null;
    kind: ApprovalKind;
  } | null;
}

export interface ApprovalQueueResponse {
  approvals: ApprovalQueueItem[];
}

export interface ApprovalSettingsUser {
  userId: string;
  displayName: string | null;
  email: string;
  active?: boolean;
}

export interface ApprovalSettings {
  processingUserIds: string[];
  preapprovalUserIds: string[];
  orderApprovalUserIds: string[];
  quoteApprovalUserIds: string[];
  wirelessPortalAccountUserIds: string[];
  userOptions: ApprovalSettingsUser[];
  updatedAt: string | null;
}

export interface ApprovalLocationOption {
  id: string | number;
  abbreviation: string | null;
  name: string | null;
}

export const INSIDE_SALES_APPROVALS_QUERY_KEY = ["inside-sales", "approvals"] as const;
export const INSIDE_SALES_APPROVAL_SETTINGS_QUERY_KEY = ["inside-sales", "approvals", "settings"] as const;

export function fetchInsideSalesApprovals(): Promise<ApprovalQueueResponse> {
  return apiFetch<ApprovalQueueResponse>(APPROVALS_BASE);
}

export function fetchInsideSalesApproval(id: string): Promise<ApprovalDetailResponse> {
  return apiFetch<ApprovalDetailResponse>(`${APPROVALS_BASE}/${encodeURIComponent(id)}`);
}

export function assignInsideSalesApproval(id: string, assigneeUserId: string): Promise<ApprovalDetailResponse> {
  return apiFetch<ApprovalDetailResponse>(`${APPROVALS_BASE}/${encodeURIComponent(id)}/assign`, {
    method: "POST",
    body: { assigneeUserId },
  });
}

export function transitionInsideSalesApproval(
  id: string,
  input: { action: ApprovalTransitionAction; note?: string; locationId?: string },
): Promise<ApprovalDetailResponse> {
  return apiFetch<ApprovalDetailResponse>(`${APPROVALS_BASE}/${encodeURIComponent(id)}/transition`, {
    method: "POST",
    body: input,
  });
}

export function deleteInsideSalesApproval(id: string): Promise<void> {
  return apiFetch<void>(`${APPROVALS_BASE}/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function linkInsideSalesApproval(
  id: string,
  link: { salesOrderId: string } | { quoteId: string },
): Promise<ApprovalDetailResponse> {
  return apiFetch<ApprovalDetailResponse>(`${APPROVALS_BASE}/${encodeURIComponent(id)}/link`, {
    method: "POST",
    body: link,
  });
}

export function commentInsideSalesApproval(id: string, body: string): Promise<ApprovalDetailResponse> {
  return apiFetch<ApprovalDetailResponse>(`${APPROVALS_BASE}/${encodeURIComponent(id)}/comments`, {
    method: "POST",
    body: { body },
  });
}

export function patchInsideSalesApproval(
  id: string,
  input: {
    name?: string;
    description?: string | null;
    type?: ApprovalKind;
    sourceTicketUrl?: string | null;
  },
): Promise<ApprovalDetailResponse> {
  return apiFetch<ApprovalDetailResponse>(`${APPROVALS_BASE}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: input,
  });
}

export function addInsideSalesApprovalAttachment(
  id: string,
  input: { url: string; label?: string },
): Promise<ApprovalDetailResponse> {
  return apiFetch<ApprovalDetailResponse>(`${APPROVALS_BASE}/${encodeURIComponent(id)}/attachments`, {
    method: "POST",
    body: input,
  });
}

export interface ApprovalDocumentOption {
  id: string;
  number: string;
  clientName: string | null;
  endUserName: string | null;
  date: string | null;
  statusTone: "approved" | "pending";
}

export function searchInsideSalesApprovalDocuments(
  kind: ApprovalKind,
  search: string,
): Promise<{ documents: ApprovalDocumentOption[] }> {
  return apiFetch<{ documents: ApprovalDocumentOption[] }>(`${APPROVALS_BASE}/document-options`, {
    query: { kind, search: search.trim() || undefined },
  });
}

export function uploadInsideSalesApprovalDocument(id: string, file: File): Promise<ApprovalDetailResponse> {
  const formData = new FormData();
  formData.append("file", file);
  return apiUploadFormData<ApprovalDetailResponse>(`${APPROVALS_BASE}/${encodeURIComponent(id)}/documents`, formData);
}

export function approvalDocumentPreviewEndpoint(approvalId: string, documentId: string): string {
  return `${APPROVALS_BASE}/${encodeURIComponent(approvalId)}/documents/${encodeURIComponent(documentId)}`;
}

export async function fetchApprovalDocumentPreview(approvalId: string, documentId: string): Promise<string> {
  const result = await apiFetchBlob(approvalDocumentPreviewEndpoint(approvalId, documentId));
  return URL.createObjectURL(result.blob);
}

export function fetchInsideSalesApprovalSettings(): Promise<ApprovalSettings> {
  return apiFetch<ApprovalSettings>(`${APPROVALS_BASE}/settings`);
}

export function patchInsideSalesApprovalSettings(
  input: Pick<
    ApprovalSettings,
    | "processingUserIds"
    | "preapprovalUserIds"
    | "orderApprovalUserIds"
    | "quoteApprovalUserIds"
    | "wirelessPortalAccountUserIds"
  >,
): Promise<ApprovalSettings> {
  return apiFetch<ApprovalSettings>(`${APPROVALS_BASE}/settings`, {
    method: "PATCH",
    body: input,
  });
}

export function fetchApprovalLocationOptions(): Promise<{ locations: ApprovalLocationOption[] }> {
  return apiFetch<{ locations: ApprovalLocationOption[] }>("/api/sales-orders/locations");
}
